import { readAdminToken } from './adminToken';

export const TEACHER_SESSION_STORAGE_KEY = 'gymgpt.teacherSession';
export const TEACHER_SESSION_HEADER = 'X-Teacher-Session';

export interface TeacherSession {
  token: string;
  expiresAt: string;
}

export type TeacherUnauthorizedReason = 'expired';

type TeacherUnauthorizedListener = (reason: TeacherUnauthorizedReason) => void;

const listeners = new Set<TeacherUnauthorizedListener>();

export function readTeacherSession(): TeacherSession | null {
  try {
    const raw = sessionStorage.getItem(TEACHER_SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed
      || typeof parsed !== 'object'
      || typeof (parsed as TeacherSession).token !== 'string'
      || typeof (parsed as TeacherSession).expiresAt !== 'string'
    ) {
      return null;
    }
    const session = parsed as TeacherSession;
    return session.token.trim() ? session : null;
  } catch {
    return null;
  }
}

export function writeTeacherSession(session: TeacherSession): void {
  try {
    sessionStorage.setItem(TEACHER_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* sessionStorage unavailable */
  }
}

export function clearTeacherSession(): void {
  try {
    sessionStorage.removeItem(TEACHER_SESSION_STORAGE_KEY);
  } catch {
    /* sessionStorage unavailable */
  }
}

export function subscribeTeacherUnauthorized(listener: TeacherUnauthorizedListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function notifyTeacherUnauthorized(reason: TeacherUnauthorizedReason): void {
  clearTeacherSession();
  for (const listener of listeners) {
    listener(reason);
  }
}

export function isTeacherOnlyDashboard(): boolean {
  return Boolean(readTeacherSession()) && !readAdminToken();
}

/** Live prep/profile need `gymgpt.teacherSession`. Mock mode still uses `isTeacher`. */
export function canAccessTeacherAccount(isTeacher: boolean, useLiveAuth: boolean): boolean {
  if (readTeacherSession()) {
    return true;
  }
  return !useLiveAuth && isTeacher && !readAdminToken();
}
